// SPDX-License-Identifier: GPL-3.0-or-later
// The stream overlay page (/overlay). It is read-only: it opens WS /api/overlay/ws, applies the
// frames with `reduceOverlay`, and draws them. Layout A is the 1280 x 146 strip. Layout B is the
// 480 x 270 card (`/overlay?layout=card`). The page shows nothing while no featured turn runs
// (the page background is transparent). Every donor string is set with `textContent`.

import { OVERLAY_KEY_MAX_LEN, OVERLAY_KEY_PARAM, ROUTES } from "../src/shared/contracts.ts";
import { asList, asText, slot } from "./dom.ts";
import {
  initialOverlayState,
  type OverlayState,
  parseOverlayMessage,
  reduceOverlay,
} from "./overlay-model.ts";
import { type OverlaySlots, renderHowTo, renderOverlay } from "./overlay-view.ts";

/** The first reconnect delay. It doubles up to RECONNECT_MAX_MS. */
export const RECONNECT_MIN_MS = 1000;
export const RECONNECT_MAX_MS = 30_000;
/** The overlay sends a ping at this interval. */
const PING_MS = 25_000;
/** The page draws again at this interval, so the banner and the cards expire. */
const REDRAW_MS = 250;
/** The Worker closes an overlay socket that a newer one replaced with this code (src/worker/limits.ts). */
export const REPLACED_CLOSE_CODE = 4001;
/** After a "replaced" close, the page waits at least this long. */
export const REPLACED_WAIT_MIN_MS = 5000;
/** A random part of 0 to this many ms is added to that wait, so the old pages do not retry together. */
export const REPLACED_WAIT_JITTER_MS = 5000;

/** The next reconnect delay after a failed try. */
export function nextDelay(current: number): number {
  return Math.min(RECONNECT_MAX_MS, Math.max(RECONNECT_MIN_MS, current * 2));
}

/**
 * Plan the next reconnect after a close. `random` is a number from 0 up to (not including) 1.
 * Close code 4001 means a newer overlay replaced this one: wait 5 s or more, with jitter.
 * Any other close waits the current delay, and the next delay doubles.
 */
export function planReconnect(
  closeCode: number,
  current: number,
  random: number,
): { readonly waitMs: number; readonly nextDelayMs: number } {
  if (closeCode === REPLACED_CLOSE_CODE) {
    const r = Number.isFinite(random) ? Math.min(1, Math.max(0, random)) : 0;
    return {
      waitMs: REPLACED_WAIT_MIN_MS + Math.floor(r * REPLACED_WAIT_JITTER_MS),
      nextDelayMs: current,
    };
  }
  return { waitMs: current, nextDelayMs: nextDelay(current) };
}

/** The layout of the page: "card" for `?layout=card`, else "strip". */
export function layoutOf(search: string): "strip" | "card" {
  return new URLSearchParams(search).get("layout") === "card" ? "card" : "strip";
}

/**
 * The overlay key of the page URL (`?k=<key>`), or an empty string when the URL has none. The OBS
 * source URL carries the key (decision D14). The key is a secret: the page never shows it. The
 * field name and the length cap come from the contract (OVERLAY_KEY_PARAM, OVERLAY_KEY_MAX_LEN).
 */
export function overlayKeyOf(search: string): string {
  return (new URLSearchParams(search).get(OVERLAY_KEY_PARAM) ?? "").slice(0, OVERLAY_KEY_MAX_LEN);
}

/**
 * The WebSocket URL of the overlay route for a page location. A page with a key passes it as the
 * `k` query field (OVERLAY_KEY_PARAM). The Worker gives a socket with the right key a reserved
 * slot. No key: no field, and the socket counts against the public cap.
 */
export function overlaySocketUrl(loc: {
  readonly protocol: string;
  readonly host: string;
  readonly search: string;
}): string {
  const scheme = loc.protocol === "https:" ? "wss:" : "ws:";
  const key = overlayKeyOf(loc.search);
  const query = key === "" ? "" : `?${OVERLAY_KEY_PARAM}=${encodeURIComponent(key)}`;
  return `${scheme}//${loc.host}${ROUTES.overlayWs}${query}`;
}

/** Start the overlay page on a loaded document. */
export function bootOverlayPage(doc: Document, win: Window & typeof globalThis): void {
  const q = (name: string): HTMLElement => slot(doc, name);
  const slots: OverlaySlots = {
    root: asText(q("overlay")),
    banner: asText(q("banner")),
    bannerText: asText(q("banner-text")),
    live: asText(q("live")),
    whoName: asText(q("who-name")),
    scoreValue: asText(q("score-value")),
    ballText: asText(q("ball-text")),
    multiplierText: asText(q("multiplier-text")),
    modeName: asText(q("mode-name")),
    eventText: asText(q("event-text")),
    ballsInPlay: asText(q("balls-in-play")),
    howto: asList(q("howto")),
    queueText: asText(q("queue-text")),
    result: asText(q("result")),
    resultScore: asText(q("result-score")),
    resultThanks: asText(q("result-thanks")),
    thanks: asText(q("thanks")),
    thanksText: asText(q("thanks-text")),
  };
  q("overlay").setAttribute("data-layout", layoutOf(win.location.search));

  let state: OverlayState = initialOverlayState();
  let howtoShown: OverlayState["howto"] = null;
  const draw = (): void => {
    renderOverlay(state, Date.now(), slots);
    if (state.howto !== howtoShown) {
      howtoShown = state.howto;
      renderHowTo(state, slots);
    }
  };
  renderHowTo(state, slots);

  let delay = RECONNECT_MIN_MS;
  let pingTimer: number | undefined;

  const connect = (): void => {
    const socket = new win.WebSocket(overlaySocketUrl(win.location));
    socket.onopen = () => {
      delay = RECONNECT_MIN_MS;
      pingTimer = win.setInterval(() => {
        if (socket.readyState === 1) socket.send(JSON.stringify({ type: "ping" }));
      }, PING_MS);
    };
    socket.onmessage = (ev: MessageEvent) => {
      const msg = parseOverlayMessage(ev.data);
      if (msg === null) return;
      state = reduceOverlay(state, msg, Date.now());
      draw();
    };
    socket.onclose = (ev: CloseEvent) => {
      if (pingTimer !== undefined) win.clearInterval(pingTimer);
      pingTimer = undefined;
      // Code 4001: the Worker closed the oldest socket at its limit. Wait 5 s or more, with jitter,
      // so this page does not fight the newer ones.
      const plan = planReconnect(ev.code, delay, Math.random());
      win.setTimeout(connect, plan.waitMs);
      delay = plan.nextDelayMs;
    };
    socket.onerror = () => {
      // The close event follows.
    };
  };

  win.setInterval(draw, REDRAW_MS);
  draw();
  connect();
}
